/**
 * Keeps the offer picker and the buy-now button in sync, and reveals the
 * sticky bar once the main button scrolls out of view.
 *
 * Everything here is an enhancement: the picker is native radio inputs and the
 * button renders the default tier's price server-side, so the page still sells
 * with this file blocked.
 */
(function () {
  if (window.fkPickerReady) return;
  window.fkPickerReady = true;

  const PICKER_INPUT = 'variant-picker input[data-variant-id]';

  // Microsoft Clarity tagging (no-op when the Clarity app block is absent).
  // "set" tags stick to the session so recordings and heatmaps can be filtered
  // by tier / CTA placement; "event" marks the moment in the recording.
  function track(name, tags) {
    if (typeof window.clarity !== 'function') return;
    try {
      Object.keys(tags || {}).forEach(function (key) {
        window.clarity('set', key, String(tags[key]));
      });
      window.clarity('event', name);
    } catch (e) {}
  }

  // The card the visitor touched last, anywhere on the page. A button in a
  // section without its own picker (the closing CTA) charges that card, so
  // the page never sells one tier above and another below.
  let lastPicked = null;

  function selectedInput(button) {
    const scope = button.closest('.shopify-section') || document;
    const local = scope.querySelector(PICKER_INPUT + ':checked');
    if (local) return local;
    if (lastPicked && lastPicked.checked) return lastPicked;
    return null;
  }

  function selectedVariant(button) {
    const checked = selectedInput(button);
    return checked ? checked.dataset.variantId : button.dataset.variantId;
  }

  document.addEventListener('click', function (event) {
    const button = event.target.closest('[data-fk-buy-now]');
    if (!button) return;
    const id = selectedVariant(button);
    if (!id) return;
    button.setAttribute('aria-busy', 'true');
    const checked = selectedInput(button);
    track('buy_now_click', {
      tier: checked ? checked.value : 'default',
      variant_id: id,
      cta_placement: button.closest('[data-fk-buy-now-sticky]') ? 'sticky' : 'main',
    });
    window.location.href = '/cart/' + id + ':1';
  });

  document.addEventListener('change', function (event) {
    const input = event.target.closest(PICKER_INPUT);
    if (!input || !input.dataset.fkPrice) return;
    lastPicked = input;
    const scope = input.closest('.shopify-section') || document;
    scope.querySelectorAll('[data-fk-buy-now-price]').forEach(function (el) {
      el.textContent = input.dataset.fkPrice;
    });
    // Buttons in sections that have no picker of their own follow as well.
    document.querySelectorAll('.shopify-section').forEach(function (section) {
      if (section === scope || section.querySelector(PICKER_INPUT)) return;
      section.querySelectorAll('[data-fk-buy-now-price]').forEach(function (el) {
        el.textContent = input.dataset.fkPrice;
      });
    });
    track('tier_change', { tier: input.value });
  });

  // Gallery videos flagged data-fk-autoplay start, muted, once their slide is
  // on screen, and pause when it leaves. The file is not fetched before that,
  // so the first image keeps the connection to itself.
  function watchAutoplayVideos() {
    const medias = document.querySelectorAll('deferred-media[data-fk-autoplay]');
    if (!medias.length || !('IntersectionObserver' in window)) return;

    function videoOf(media) {
      return media.querySelector('video');
    }

    const observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          const media = entry.target;
          if (entry.isIntersecting) {
            if (!media.getAttribute('data-media-loaded') && typeof media.loadContent === 'function') {
              media.loadContent(false);
            }
            const video = videoOf(media);
            if (!video) return;
            video.muted = true;
            video.setAttribute('muted', '');
            video.setAttribute('playsinline', '');
            video.setAttribute('loop', '');
            const playing = video.play();
            if (playing && playing.catch) playing.catch(function () {});
          } else {
            const video = videoOf(media);
            if (video && !video.paused) video.pause();
          }
        });
      },
      { threshold: 0.6 }
    );

    medias.forEach(function (media) {
      // The poster button is for visitors without autoplay; the video is the
      // control here, so the play glyph only confuses.
      const poster = media.querySelector('.deferred-media__poster-icon');
      if (poster) poster.hidden = true;
      observer.observe(media);
    });
  }

  // Deferred images (gallery slides after the first, UGC cards) ship a 32px
  // placeholder with the real srcset in data attributes, so nothing competes
  // with the hero image for the connection. They are swapped in:
  //   - gallery slides: as soon as the hero image has loaded, all of them,
  //     so a fast swipe never lands on a blank slide;
  //   - cards flagged data-fk-near: when they come within 300px of the viewport.
  // On a phone the candidates stop at 832px, as they do for the hero: at 3x a
  // slide would otherwise cost 250-300 KB each for detail no one sees.
  function mobileSrcset(srcset) {
    if (window.innerWidth >= 750) return srcset;
    return srcset
      .split(',')
      .filter(function (candidate) {
        const match = candidate.trim().match(/\s(\d+)w$/);
        return !match || Number(match[1]) <= 832;
      })
      .join(',');
  }

  function swapDeferredImage(img) {
    if (!img.dataset.fkSrcset) return;
    img.sizes = img.dataset.fkSizes || '100vw';
    img.srcset = mobileSrcset(img.dataset.fkSrcset);
    img.loading = 'eager';
    img.removeAttribute('data-fk-srcset');
    img.removeAttribute('data-fk-sizes');
    function reveal() {
      img.classList.remove('fk-deferred-image');
    }
    img.addEventListener('load', reveal, { once: true });
    img.addEventListener('error', reveal, { once: true });
    // Whatever happens, no slide stays blurred.
    setTimeout(reveal, 5000);
  }

  function watchDeferredImages() {
    const slides = Array.prototype.slice.call(
      document.querySelectorAll('media-gallery img[data-fk-srcset]:not([data-fk-near])')
    );
    const near = Array.prototype.slice.call(document.querySelectorAll('img[data-fk-srcset][data-fk-near]'));

    // The two slides next to the hero load once the page has painted and the
    // browser is idle, so the first swipes land on finished pictures. They
    // used to load on the hero's `load` event, but the hero is preloaded at
    // high priority and finishes before the render-blocking stylesheet on a
    // slow connection, so 200 KB of pictures nobody could see yet were
    // competing with the CSS for the first paint. Now they wait for the
    // hero, the stylesheet (window `load` covers both) or the visitor's first
    // interaction, whichever comes first, and then for an idle slot.
    // The rest wait for the visitor's first interaction anywhere on the page:
    // a touch, a click, a scroll, a key, a wheel. Nobody reaches slide 4
    // without one of those, and a swipe takes longer than an 80 KB download.
    // A visitor who never interacts never pays for five pictures they never see.
    const nextSlides = slides.slice(0, 2);
    const restSlides = slides.slice(2);
    let nextLoaded = false;

    function loadNext() {
      if (nextLoaded) return;
      nextLoaded = true;
      window.requestIdleCallback(
        function () {
          nextSlides.forEach(swapDeferredImage);
        },
        { timeout: 1000 }
      );
    }
    function loadRest() {
      loadNext();
      restSlides.forEach(swapDeferredImage);
    }
    if (slides.length) {
      if (document.readyState === 'complete') {
        loadNext();
      } else {
        window.addEventListener('load', loadNext, { once: true });
        // A load event that never fires (a hung third-party script) must not
        // hold the gallery hostage.
        setTimeout(loadNext, 4000);
      }
      ['touchstart', 'pointerdown', 'mousedown', 'keydown', 'wheel', 'scroll'].forEach(function (type) {
        window.addEventListener(type, loadRest, { once: true, passive: true, capture: true });
      });
    }

    if (near.length) {
      if (!('IntersectionObserver' in window)) {
        near.forEach(swapDeferredImage);
        return;
      }
      const observer = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (!entry.isIntersecting) return;
            swapDeferredImage(entry.target);
            observer.unobserve(entry.target);
          });
        },
        { rootMargin: '300px 0px' }
      );
      near.forEach(function (img) {
        observer.observe(img);
      });
    }
  }

  function watchStickyBars() {
    if (!('IntersectionObserver' in window)) return;
    document.querySelectorAll('[data-fk-buy-now-sticky]').forEach(function (bar) {
      if (bar.dataset.fkObserved === 'true') return;
      const previous = bar.previousElementSibling;
      const main = previous ? previous.querySelector('[data-fk-buy-now]') : null;
      if (!main) return;
      bar.dataset.fkObserved = 'true';
      new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            bar.hidden = entry.isIntersecting || entry.boundingClientRect.top > 0;
          });
        },
        { threshold: 0 }
      ).observe(main);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', watchStickyBars);
  } else {
    watchStickyBars();
    watchAutoplayVideos();
    watchDeferredImages();
  }

  document.addEventListener('shopify:section:load', watchStickyBars);
})();
